import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Field } from 'react-final-form';
import createDecorator from 'final-form-calculate';
import { v4 as uuidv4 } from 'uuid';
import orderBy from 'lodash/orderBy';

import useUpdateQuickStartGuideFlag from 'Dashboard/components/QuickStartGuide/useUpdateQuickStartGuideFlag';
import { QuickStartGuideItemIDs } from 'Dashboard/components/QuickStartGuide/QuickStartGuideItems.constants';

import { getWorkspaceNotificationCards } from 'Shared/actions/notifications';
import { createFeedback, updateFeedback } from 'Shared/actions/Feedbacks.actions';
import { multipleRequired } from 'Shared/utils/formValidations';
import {
  FeedbackRecognitionRequestTypes,
  FeedbackRecognitionType,
  FeedbackRecognitionTemplateTypes,
} from 'Shared/constants/types';
import FieldGroup from 'Shared/components/FormComponents/FieldGroup/FieldGroup';
import FormSelect from 'Shared/components/FormComponents/FormSelect';
import FormTextarea from 'Shared/components/FormComponents/FormTextarea';
import { WpModal, WpModalBody } from 'Wpds/components/modal';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { clickedKudosFormEvent } from 'Shared/constants/constants';

import FormVideoRecorder from '../../FormComponents/FormVideoRecorder';
import FormGifPicker from '../../FormComponents/FormGifPicker';
import Header from './components/Header';
import FormPublicOrPrivate from './components/FormPublicOrPrivate';
import { formValidate } from './utils';

import { fetchUserSuggestions } from 'Shared/actions/UserSuggestions.actions';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

import './GiveRecognitionModal.scss';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  isOpen: boolean;
  onModalToggle;
  onSuccess?(): void;
  choosenRequest?: {
    id?: number;
    workspaceUuid: string;
    employee: {
      id: number;
    };
  };
  userSuggestionId?: string;
  feedback?: FeedbackRecognitionType;
};

function getIsPublic(showPublicPrivate, onlyShowPublicOption, feedbackExist) {
  if (feedbackExist) return false;
  if (showPublicPrivate) {
    if (onlyShowPublicOption) return true;
    return null;
  }
  return false;
}

const decorator = createDecorator({
  field: 'isPublic',
  updates: {
    chatOptions: () => ({
      shareToChat: null,
      channel: null,
    }),
  },
});

function GiveRecognitionModal({ choosenRequest, feedback, isOpen, onModalToggle, userSuggestionId, onSuccess }: Props) {
  const dispatch = useDispatch();
  const { isZoom } = useApp();
  const { handleCompleteItem } = useUpdateQuickStartGuideFlag();
  const { tr } = useTranslation();

  const workspace = useSelector(state => state.workspaces.find(({ id }) => id === choosenRequest?.workspaceUuid));
  const workspaceIsDefined = !!workspace;
  const workspaceIsGroup = workspaceIsDefined && workspace?.isGroup;
  const workspaceIsPersonal = workspaceIsDefined && workspace?.isPersonal;
  const workspaceIsTeammate = workspaceIsDefined && !workspaceIsGroup && !workspaceIsPersonal;
  const workspaceName = workspaceIsDefined ? workspace.name : null;

  const usersInWorkspace = workspaceIsDefined ? workspace.users : null;
  const { id: currentUserId } = useCurrentUser();
  const otherUserNotInvited = workspaceIsTeammate && !usersInWorkspace.find(({ id }) => id !== currentUserId);

  const initialTeammate = workspaceIsGroup ? { id: null } : choosenRequest?.employee;
  const workspacesWithInitialTeammate = useSelector(state =>
    orderBy(
      state.workspaces.filter(({ users }) => users.find(({ id }) => id === initialTeammate?.id)),
      'isOneOnOne',
      'desc'
    )
  );
  const nonTeamSyncWorkspacesWithInitialTeammate = workspacesWithInitialTeammate.filter(
    ({ isTeamSync }) => !isTeamSync
  );
  const teamSyncWorkspaceWithInitialTeammate = workspacesWithInitialTeammate.find(({ isTeamSync }) => isTeamSync);
  const onlyShowPublicOption = nonTeamSyncWorkspacesWithInitialTeammate.length === 0 && !workspaceIsDefined;
  const handleCancelButtonClick = useCallback(() => {
    onModalToggle();
    (window as any).analytics.track(clickedKudosFormEvent, { target: 'close_button' });
  }, [onModalToggle]);
  const showPublicPrivate = !otherUserNotInvited;

  const handleSave = useCallback(
    async values => {
      (window as any).analytics.track(clickedKudosFormEvent, {
        target: 'submit_kudos_button',
        hasGif: Boolean(values.gif?.fixed_width?.url),
        hasVideo: Boolean(values.video),
        isPublic: values.isPublic,
        isEdit: false,
      });

      const receiverIds = values.teammates ? values.teammates.map(({ value }) => value) : [values.teammate];

      const responses = [];

      const workspaceId = (() => {
        if (workspace?.id) return workspace.id;
        // There is an edge case where someone selects 'public' but they or the creator is not in the
        // all hands workspace. In this case, we use the first non all hands workspace
        if (values.isPublic) return teamSyncWorkspaceWithInitialTeammate?.id || values.workspaceId;
        if (values.workspaceId) return values.workspaceId;
      })();

      for (let response_index = 0; response_index < receiverIds.length; response_index++) {
        const response = await dispatch(
          createFeedback({
            uuid: uuidv4(),
            creatorId: currentUserId,
            receiverId: receiverIds[response_index],
            requestType: FeedbackRecognitionRequestTypes.recognition,
            feedbackRecognitionRequestId: choosenRequest?.id,
            workspaceId: workspaceId,
            likeThatText: values.text,
            likeThatVideo: values.video,
            isPublic: values.isPublic,
            gifUrl: values.gif?.fixed_width?.url,
            shareOnChatData: values.chatOptions,
            template_type: FeedbackRecognitionTemplateTypes.kudosDefault,
            userSuggestionId,
          })
        );
        responses.push(response);
      }
      if (responses.filter(x => x).length === receiverIds.length) {
        if (userSuggestionId) dispatch(fetchUserSuggestions());
        handleCompleteItem(QuickStartGuideItemIDs.giveKudos);
        dispatch(getWorkspaceNotificationCards());
        if (onSuccess) onSuccess();
        onModalToggle();
      }
      return responses;
    },
    [choosenRequest, currentUserId, dispatch, onModalToggle]
  );

  const handleUpdate = useCallback(
    values => {
      (window as any).analytics.track(clickedKudosFormEvent, {
        target: 'submit_kudos_button',
        hasGif: Boolean(values.gif?.fixed_width?.url),
        hasVideo: Boolean(values.video),
        isPublic: values.isPublic,
        isEdit: true,
      });
      return dispatch(
        updateFeedback({
          uuid: feedback.uuid,
          likeThatText: values.text,
          likeThatVideo: values.video,
          gifUrl: values.gif?.fixed_width?.url,
          isDraft: feedback.isDraft,
        })
      ).then(onModalToggle);
    },
    [dispatch, onModalToggle] //eslint-disable-line
  );

  const initialValues = useMemo(
    () => ({
      teammate: feedback?.receiverId || initialTeammate?.id,
      teammates: feedback?.receiverId,
      text: feedback?.likeThatText || '',
      video: feedback?.likeThatVideo || null,
      gif: { fixed_width: { url: feedback?.gifUrl } },
      isPublic: getIsPublic(showPublicPrivate, onlyShowPublicOption, !!feedback),
      chatOptions: {
        shareToChat: null,
        channel: null,
      },
      workspaceId: nonTeamSyncWorkspacesWithInitialTeammate?.[0]?.id,
    }),
    []
  );

  const selectOptions = usersInWorkspace
    ?.map(user => ({ value: user.id, label: user.displayName }))
    ?.filter(option => option.value !== currentUserId);

  if (!choosenRequest && !feedback) return null;

  return (
    <WpModal isOpen={isOpen} onToggle={handleCancelButtonClick} dismissOnBackdrop={false}>
      <Header
        teammateId={feedback?.receiverId || initialTeammate?.id}
        onModalToggle={onModalToggle}
        isGroup={workspaceIsGroup}
        isEditFeedback={Boolean(feedback)}
        workspaceName={workspaceName}
      />
      <WpModalBody>
        <Form
          subscription={{ pristine: true, submitting: true, errors: true }}
          initialValues={initialValues}
          decorators={!workspaceIsGroup ? [decorator] : null}
          validate={formValidate}
          onSubmit={feedback ? handleUpdate : handleSave}
        >
          {({ handleSubmit, submitting, pristine, errors }) => {
            const errorsExist = !!Object.keys(errors).length;
            return (
              <form onSubmit={handleSubmit}>
                <div className="GiveRecognitionModal">
                  {workspaceIsGroup && !feedback ? (
                    <FieldGroup
                      required
                      className="mt-0 mb-3"
                      label={tr('perform.give_recognition_modal.who_to_give_kudos')}
                      labelClassName="GiveRecognitionModal__label"
                    >
                      <Field
                        name="teammates"
                        validate={multipleRequired}
                        options={selectOptions}
                        isMulti
                        customGetOutputValue={fieldValue => fieldValue}
                        component={FormSelect}
                      />
                    </FieldGroup>
                  ) : (
                    <Field hidden name="teammate" type="text" component="input" />
                  )}

                  <FieldGroup
                    label={tr('perform.give_recognition_modal.leave_a_compliment')}
                    className="mt-0"
                    labelClassName="GiveRecognitionModal__label"
                  />

                  {!isZoom && <Field name="video" component={FormVideoRecorder} />}
                  <Field name="text" textareaClassName="GiveRecognitionModal__textarea mt-2" component={FormTextarea} />

                  <FieldGroup
                    label={tr('perform.give_recognition_modal.choose_gif')}
                    labelClassName="GiveRecognitionModal__label"
                  />
                  <Field name="gif" component={FormGifPicker} />

                  {!feedback && showPublicPrivate ? (
                    <>
                      {onlyShowPublicOption && (
                        <div className="mt-3">{tr('perform.give_recognition_modal.visible_to_organization')}</div>
                      )}
                      <FieldGroup
                        label={
                          onlyShowPublicOption
                            ? tr('perform.give_recognition_modal.share_to_chat')
                            : tr('perform.give_recognition_modal.public_or_private')
                        }
                        labelClassName="GiveRecognitionModal__label"
                      />
                      <Field
                        allowNull
                        name="isPublic"
                        workspaceIsGroup={workspaceIsGroup}
                        workspaceIsDefined={workspaceIsDefined}
                        nonTeamSyncWorkspacesWithInitialTeammate={nonTeamSyncWorkspacesWithInitialTeammate}
                        onlyShowPublicOption={onlyShowPublicOption}
                        component={FormPublicOrPrivate}
                      />
                    </>
                  ) : null}
                </div>
                <div className="my-4">
                  <WpButtonGroup>
                    <WpButton
                      loading={submitting}
                      disabled={pristine || errorsExist}
                      qaTest="submit-kudos-button"
                      btnType="submit"
                    >
                      {tr('perform.give_recognition_modal.give_kudos')}
                    </WpButton>
                    <WpButton variant="tertiary" onClick={handleCancelButtonClick}>
                      {tr('perform.common.cancel')}
                    </WpButton>
                  </WpButtonGroup>
                </div>
              </form>
            );
          }}
        </Form>
      </WpModalBody>
    </WpModal>
  );
}

export default GiveRecognitionModal;
